import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { commandDashboardTaskStatusesQuery } from '../../api/command-dashboard-api';
import { ApiException } from '../../../types';

/** The refresh endpoints throw a showable when a run is already in flight for this domain. */
export function isCommandDashboardRefreshAlreadyRunning(
  error: unknown
): boolean {
  const data = (error as { response?: { data?: ApiException } })?.response
    ?.data;
  return Boolean(
    data?.isShowable && data.message?.toLowerCase().includes('already running')
  );
}

/**
 * Shared RUNNING flag plus the fallback that lands a new snapshot when Firebase
 * does not: nightly dispatch, another admin's refresh, or a dropped push.
 * `upsert(IDLE)` deletes the TaskStatus row, so completion is RUNNING -> missing,
 * not a literal IDLE value. The first observation only seeds the ref so a tile
 * that mounts mid-refresh does not immediately invalidate.
 */
export function useCommandDashboardTileTaskStatus(
  taskId: string,
  metricsQueryKey: string,
  enabled = true
) {
  const queryClient = useQueryClient();
  const statusQuery = useQuery(commandDashboardTaskStatusesQuery(enabled));
  const isRefreshing = statusQuery.data?.[taskId] === 'RUNNING';
  const wasRefreshingRef = useRef(false);

  useEffect(() => {
    if (wasRefreshingRef.current && !isRefreshing) {
      queryClient.invalidateQueries({ queryKey: [metricsQueryKey] });
    }
    wasRefreshingRef.current = isRefreshing;
  }, [isRefreshing, metricsQueryKey, queryClient]);

  return { statusQuery, isRefreshing };
}
